// src/pages/boom-sprayer/master-data/DataPJWilayah.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    IconButton,
    Stack,
    Snackbar,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import { useTheme } from "@mui/material/styles";
import '../../../components/css/ded-style.css';
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue, set, remove, push } from "firebase/database";
import * as XLSX from "xlsx";

// Import formatters dari file utilitas
import {
    formatText
} from "../../../utils/formatters-master";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../utils/formatLastUpdated";

import CustomBreadcrumbs from "../../../components/universal-features/breadcrumbs";

// Import CustomTableContainer dari komponen terpisah
import CustomTableContainer from "../../../components/CustomScrollContainer";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

function DataPJWilayah() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [editingRow, setEditingRow] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [lastUpdated, setLastUpdated] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [isAddIconRotated, setIsAddIconRotated] = useState(false);
    
    // State untuk data referensi
    const [kepalaWilayahList, setKepalaWilayahList] = useState([]);
    const [plantCareList, setPlantCareList] = useState([]);
    const [fruitCareList, setFruitCareList] = useState([]);
    
    // State untuk data baru
    const [newData, setNewData] = useState({
        lokasi: '',
        id_kepala_wilayah: '',
        id_kepala_kebun_plant_care: '',
        id_kepala_kebun_fruit_care: ''
    });

    // Fungsi untuk mendapatkan nama berdasarkan ID
    const getNamaKepalaWilayah = (id) => {
        const kepalaWilayah = kepalaWilayahList.find(item => item.id_kepala_wilayah === id);
        return kepalaWilayah ? kepalaWilayah.nama_kepala_wilayah : '';
    };

    const getNamaPlantCare = (id) => {
        const plantCare = plantCareList.find(item => item.id_kepala_kebun_plant_care === id);
        return plantCare ? plantCare.nama_kepala_kebun_plant_care : '';
    };

    const getNamaFruitCare = (id) => {
        const fruitCare = fruitCareList.find(item => item.id_kepala_kebun_fruit_care === id);
        return fruitCare ? fruitCare.nama_kepala_kebun_fruit_care : '';
    };

    // Fungsi untuk mendapatkan ID berdasarkan nama
    const getIdKepalaWilayahByName = (nama) => {
        const kepalaWilayah = kepalaWilayahList.find(item => 
            item.nama_kepala_wilayah.toLowerCase().trim() === nama.toLowerCase().trim()
        );
        return kepalaWilayah ? kepalaWilayah.id_kepala_wilayah : '';
    };

    const getIdPlantCareByName = (nama) => {
        const plantCare = plantCareList.find(item => 
            item.nama_kepala_kebun_plant_care.toLowerCase().trim() === nama.toLowerCase().trim()
        );
        return plantCare ? plantCare.id_kepala_kebun_plant_care : '';
    };

    const getIdFruitCareByName = (nama) => {
        const fruitCare = fruitCareList.find(item => 
            item.nama_kepala_kebun_fruit_care.toLowerCase().trim() === nama.toLowerCase().trim()
        );
        return fruitCare ? fruitCare.id_kepala_kebun_fruit_care : '';
    };

    // Fungsi untuk mengecek apakah lokasi sudah ada (case-insensitive)
    const isLokasiExists = (lokasiToCheck) => {
        if (!lokasiToCheck || !originalDataList) return false;
        
        const lokasiLower = lokasiToCheck.toLowerCase().trim();
        return originalDataList.some(item => 
            item.lokasi.toLowerCase().trim() === lokasiLower
        );
    };

    useEffect(() => {
        const fetchAllData = async () => {
            setLoadingData(true);
            try {
                // Fetch data referensi terlebih dahulu
                await fetchReferensiData();
                
                // Fetch main data
                await fetchMainData();
                
            } catch (error) {
                console.error("Error fetching all data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchAllData();
        }
    }, [user]);

    const fetchReferensiData = async () => {
        try {
            // Fetch kepala wilayah
            const kepalaWilayahRef = ref(database, "db_config/db_fresh_pineapple/tb_kepala_wilayah");
            const kepalaWilayahSnapshot = await onValuePromise(kepalaWilayahRef);
            const kepalaWilayahData = kepalaWilayahSnapshot.val();
            const kepalaWilayahArray = [];
            
            if (kepalaWilayahData) {
                Object.keys(kepalaWilayahData).forEach(key => {
                    const record = kepalaWilayahData[key];
                    kepalaWilayahArray.push({ 
                        id_kepala_wilayah: record.id_kepala_wilayah || key,
                        nama_kepala_wilayah: record.nama_kepala_wilayah || ''
                    });
                });
            }
            setKepalaWilayahList(kepalaWilayahArray);

            // Fetch plant care
            const plantCareRef = ref(database, "db_config/db_fresh_pineapple/tb_kepala_kebun_plant_care");
            const plantCareSnapshot = await onValuePromise(plantCareRef);
            const plantCareData = plantCareSnapshot.val();
            const plantCareArray = [];
            
            if (plantCareData) {
                Object.keys(plantCareData).forEach(key => {
                    const record = plantCareData[key];
                    plantCareArray.push({ 
                        id_kepala_kebun_plant_care: record.id_kepala_kebun_plant_care || key,
                        nama_kepala_kebun_plant_care: record.nama_kepala_kebun_plant_care || ''
                    });
                });
            }
            setPlantCareList(plantCareArray);

            // Fetch fruit care
            const fruitCareRef = ref(database, "db_config/db_fresh_pineapple/tb_kepala_kebun_fruit_care");
            const fruitCareSnapshot = await onValuePromise(fruitCareRef);
            const fruitCareData = fruitCareSnapshot.val();
            const fruitCareArray = [];
            
            if (fruitCareData) {
                Object.keys(fruitCareData).forEach(key => {
                    const record = fruitCareData[key];
                    fruitCareArray.push({ 
                        id_kepala_kebun_fruit_care: record.id_kepala_kebun_fruit_care || key,
                        nama_kepala_kebun_fruit_care: record.nama_kepala_kebun_fruit_care || ''
                    });
                });
            }
            setFruitCareList(fruitCareArray);

        } catch (error) {
            console.error("Error fetching referensi data:", error);
            throw error;
        }
    };

    const fetchMainData = async () => {
        try {
            // Fetch main data from db_config/db_helper/tb_pj_wilayah
            const pjWilayahRef = ref(database, "db_config/db_helper/tb_pj_wilayah");
            const pjWilayahSnapshot = await onValuePromise(pjWilayahRef);
            const pjWilayahData = pjWilayahSnapshot.val();
            const loadedData = [];
            let latestUpdateTime = null;
            
            if (pjWilayahData) {
                Object.keys(pjWilayahData).forEach(key => {
                    const record = pjWilayahData[key];
                    loadedData.push({ 
                        firebaseKey: key, 
                        lokasi: record.lokasi || '',
                        id_kepala_wilayah: record.id_kepala_wilayah || '',
                        id_kepala_kebun_plant_care: record.id_kepala_kebun_plant_care || '',
                        id_kepala_kebun_fruit_care: record.id_kepala_kebun_fruit_care || '',
                        last_updated: record.last_updated || null
                    });
                    
                    // Cari waktu update terakhir
                    if (record.last_updated) {
                        const updateTime = new Date(record.last_updated);
                        if (!latestUpdateTime || updateTime > latestUpdateTime) {
                            latestUpdateTime = updateTime;
                        }
                    }
                });
                
                // Set last updated time
                if (latestUpdateTime) {
                    setLastUpdated(latestUpdateTime);
                }
            }

            // Sort by lokasi
            loadedData.sort((a, b) => a.lokasi.localeCompare(b.lokasi));
            
            setOriginalDataList(loadedData);
            setDataList(loadedData);

        } catch (error) {
            console.error("Error fetching main data:", error);
            throw error;
        }
    };

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const filtered = originalDataList.filter(item => 
                item.lokasi.toLowerCase().includes(searchQuery.toLowerCase()) ||
                getNamaKepalaWilayah(item.id_kepala_wilayah).toLowerCase().includes(searchQuery.toLowerCase()) ||
                getNamaPlantCare(item.id_kepala_kebun_plant_care).toLowerCase().includes(searchQuery.toLowerCase()) ||
                getNamaFruitCare(item.id_kepala_kebun_fruit_care).toLowerCase().includes(searchQuery.toLowerCase())
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList, kepalaWilayahList, plantCareList, fruitCareList]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSnackbar({
                open: true,
                message: "Tidak ada file yang dipilih.",
                severity: "warning",
            });
            return;
        }

        setImportLoading(true);
        setSnackbar({
            open: true,
            message: "Memulai proses import...",
            severity: "info",
        });

        try {
            const reader = new FileReader();
            
            reader.onload = async (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });

                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    
                    // Baca data dari Excel mulai dari baris 3 (A3, B3, C3, D3)
                    // Header di A1, B1, C1, D1
                    // Data mulai dari A3
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                    
                    console.log("Data Excel:", jsonData);
                    
                    // Cari header
                    const headers = jsonData[0] || [];
                    console.log("Headers:", headers);
                    
                    // Data dimulai dari baris 2 (indeks 2 karena array mulai dari 0)
                    const dataRows = jsonData.slice(2);
                    console.log("Data rows:", dataRows);

                    // Path database
                    const dbPath = "db_config/db_helper/tb_pj_wilayah";
                    const tbRef = ref(database, dbPath);

                    // Ambil data yang sudah ada
                    const snapshot = await onValuePromise(tbRef);
                    const existingData = snapshot.exists() ? snapshot.val() : {};

                    let importedCount = 0;
                    let updatedCount = 0;
                    let ignoredCount = 0;
                    const errors = [];

                    for (const row of dataRows) {
                        try {
                            // Skip baris kosong
                            if (!row || row.length === 0 || !row[0]) {
                                ignoredCount++;
                                continue;
                            }

                            const lokasi = row[0]?.toString().trim() || '';
                            const namaKaDiv = row[1]?.toString().trim() || '';
                            const namaPlantCare = row[2]?.toString().trim() || '';
                            const namaFruitCare = row[3]?.toString().trim() || '';

                            console.log("Processing row:", { lokasi, namaKaDiv, namaPlantCare, namaFruitCare });

                            // Validasi: lokasi wajib ada
                            if (!lokasi) {
                                console.log("Lokasi kosong, diabaikan");
                                ignoredCount++;
                                continue;
                            }

                            // Cari ID berdasarkan nama
                            const idKepalaWilayah = getIdKepalaWilayahByName(namaKaDiv);
                            const idPlantCare = getIdPlantCareByName(namaPlantCare);
                            const idFruitCare = getIdFruitCareByName(namaFruitCare);

                            // Persiapkan data untuk disimpan
                            const dataToSave = {
                                lokasi: lokasi,
                                id_kepala_wilayah: idKepalaWilayah,
                                id_kepala_kebun_plant_care: idPlantCare,
                                id_kepala_kebun_fruit_care: idFruitCare,
                                last_updated: new Date().toISOString()
                            };

                            console.log("Data yang akan disimpan:", dataToSave);

                            // Cek apakah lokasi sudah ada (case-insensitive)
                            const existingKey = Object.keys(existingData).find(key => 
                                existingData[key].lokasi?.toLowerCase().trim() === lokasi.toLowerCase().trim()
                            );

                            if (existingKey) {
                                // Update data yang sudah ada
                                const existingItemRef = ref(database, `${dbPath}/${existingKey}`);
                                await set(existingItemRef, dataToSave);
                                updatedCount++;
                                console.log(`Data diperbarui: ${lokasi}`);
                            } else {
                                // Simpan sebagai data baru dengan key = lokasi
                                const newRef = ref(database, `${dbPath}/${lokasi}`);
                                await set(newRef, dataToSave);
                                importedCount++;
                                console.log(`Data baru: ${lokasi}`);
                            }

                        } catch (error) {
                            console.error("Error processing row:", error, "Row data:", row);
                            errors.push(`Error pada baris: ${JSON.stringify(row)} - ${error.message}`);
                            ignoredCount++;
                        }
                    }

                    // Refresh data setelah import
                    await fetchMainData();

                    // Tampilkan hasil import
                    let message = `Import selesai! ${importedCount} data baru, ${updatedCount} data diperbarui.`;
                    let severity = "success";
                    
                    if (ignoredCount > 0) {
                        message += ` ${ignoredCount} baris diabaikan (data kosong/tidak valid).`;
                        severity = "warning";
                    }
                    
                    if (errors.length > 0) {
                        console.error("Import errors:", errors);
                    }

                    setSnackbar({
                        open: true,
                        message: message,
                        severity: severity,
                    });

                    console.log(`Import summary: ${importedCount} baru, ${updatedCount} diperbarui, ${ignoredCount} diabaikan`);

                } catch (error) {
                    console.error("Gagal mengimpor Excel:", error);
                    setSnackbar({
                        open: true,
                        message: `Gagal membaca file Excel: ${error.message}`,
                        severity: "error",
                    });
                } finally {
                    setImportLoading(false);
                }
            };

            reader.onerror = (error) => {
                console.error("Error reading file:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal membaca file.",
                    severity: "error",
                });
                setImportLoading(false);
            };

            reader.readAsArrayBuffer(file);

        } catch (error) {
            console.error("Error in import process:", error);
            setSnackbar({
                open: true,
                message: "Terjadi kesalahan saat memproses file.",
                severity: "error",
            });
            setImportLoading(false);
        } finally {
            event.target.value = null;
        }
    };

    const handleExport = () => {
        try {
            // Siapkan data untuk export
            const exportData = dataList.map(item => ({
                'Lokasi': item.lokasi,
                'Ka.Div': getNamaKepalaWilayah(item.id_kepala_wilayah),
                'Wilayah Plant Care': getNamaPlantCare(item.id_kepala_kebun_plant_care),
                'Wilayah Fruit Care': getNamaFruitCare(item.id_kepala_kebun_fruit_care)
            }));

            // Buat worksheet
            const ws = XLSX.utils.json_to_sheet(exportData);
            
            // Buat workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Data PJ Wilayah");
            
            // Simpan file
            XLSX.writeFile(wb, `Data_PJ_Wilayah_${new Date().toISOString().split('T')[0]}.xlsx`);
            
            setSnackbar({
                open: true,
                message: "Data berhasil diexport.",
                severity: "success",
            });
            
        } catch (error) {
            console.error("Error exporting data:", error);
            setSnackbar({
                open: true,
                message: "Gagal mengexport data.",
                severity: "error",
            });
        }
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleEditClick = (row) => {
        setEditingRow(row.firebaseKey);
        setEditFormData({
            lokasi: row.lokasi,
            id_kepala_wilayah: row.id_kepala_wilayah,
            id_kepala_kebun_plant_care: row.id_kepala_kebun_plant_care,
            id_kepala_kebun_fruit_care: row.id_kepala_kebun_fruit_care
        });
    };

    const handleEditChange = (field, value) => {
        setEditFormData({
            ...editFormData,
            [field]: value
        });
    };

    const handleSaveEdit = async (firebaseKey) => {
        try {
            // Update data di Firebase
            const dataRef = ref(database, `db_config/db_helper/tb_pj_wilayah/${firebaseKey}`);
            const updatedData = {
                ...editFormData,
                last_updated: new Date().toISOString()
            };
            
            await set(dataRef, updatedData);
            
            // Update local state
            const updatedList = dataList.map(item => {
                if (item.firebaseKey === firebaseKey) {
                    return {
                        ...item,
                        ...updatedData,
                        last_updated: updatedData.last_updated
                    };
                }
                return item;
            });
            
            setDataList(updatedList);
            setOriginalDataList(updatedList);
            setEditingRow(null);
            
            setSnackbar({
                open: true,
                message: "Data berhasil diperbarui.",
                severity: "success",
            });
            
            // Update last updated time
            setLastUpdated(new Date());
            
        } catch (error) {
            console.error("Error updating data:", error);
            setSnackbar({
                open: true,
                message: "Gagal memperbarui data.",
                severity: "error",
            });
        }
    };

    const handleCancelEdit = () => {
        setEditingRow(null);
        setEditFormData({});
    };

    const handleDelete = async (firebaseKey, lokasi) => {
        if (window.confirm(`Apakah Anda yakin ingin menghapus data dengan Lokasi: ${lokasi}?`)) {
            try {
                // Hapus data dari Firebase
                const dataRef = ref(database, `db_config/db_helper/tb_pj_wilayah/${firebaseKey}`);
                await remove(dataRef);
                
                // Update local state
                const updatedList = dataList.filter(item => item.firebaseKey !== firebaseKey);
                setDataList(updatedList);
                setOriginalDataList(updatedList);
                
                setSnackbar({
                    open: true,
                    message: "Data berhasil dihapus.",
                    severity: "success",
                });
                
                // Update last updated time
                setLastUpdated(new Date());
                
            } catch (error) {
                console.error("Error deleting data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal menghapus data.",
                    severity: "error",
                });
            }
        }
    };

    const handleAddNew = () => {
        setIsAddIconRotated(!isAddIconRotated);
        setShowAddForm(!showAddForm);
    };

    const handleNewDataChange = (field, value) => {
        setNewData({
            ...newData,
            [field]: value
        });
    };

    const handleSaveNew = async () => {
        // Validasi: lokasi tidak boleh kosong
        if (!newData.lokasi.trim()) {
            setSnackbar({
                open: true,
                message: "Lokasi tidak boleh kosong.",
                severity: "warning",
            });
            return;
        }

        // Cek apakah lokasi sudah ada (case-insensitive)
        if (isLokasiExists(newData.lokasi)) {
            setSnackbar({
                open: true,
                message: `Lokasi "${newData.lokasi}" sudah ada dalam database.`,
                severity: "warning",
            });
            return;
        }

        try {
            // Simpan ke Firebase dengan key = lokasi (dengan case asli)
            const dataRef = ref(database, `db_config/db_helper/tb_pj_wilayah/${newData.lokasi}`);
            const dataToSave = {
                ...newData,
                last_updated: new Date().toISOString()
            };
            
            await set(dataRef, dataToSave);
            
            // Update local state
            const newItem = {
                firebaseKey: newData.lokasi,
                ...dataToSave
            };
            
            const updatedList = [...originalDataList, newItem].sort((a, b) => 
                a.lokasi.localeCompare(b.lokasi)
            );
            
            setDataList(updatedList);
            setOriginalDataList(updatedList);
            
            // Reset form dan state
            setNewData({
                lokasi: '',
                id_kepala_wilayah: '',
                id_kepala_kebun_plant_care: '',
                id_kepala_kebun_fruit_care: ''
            });
            setShowAddForm(false);
            setIsAddIconRotated(false);
            
            setSnackbar({
                open: true,
                message: "Data berhasil ditambahkan.",
                severity: "success",
            });
            
            // Update last updated time
            setLastUpdated(new Date());
            
        } catch (error) {
            console.error("Error adding data:", error);
            setSnackbar({
                open: true,
                message: "Gagal menambahkan data.",
                severity: "error",
            });
        }
    };

    const handleCancelNew = () => {
        setShowAddForm(false);
        setIsAddIconRotated(false);
        setNewData({
            lokasi: '',
            id_kepala_wilayah: '',
            id_kepala_kebun_plant_care: '',
            id_kepala_kebun_fruit_care: ''
        });
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Data Penanggung Jawab Wilayah
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 250 }}
                        />
                        {/* IconButton Plus/X untuk Tambah Data */}
                        <IconButton 
                            color="primary" 
                            size="small" 
                            onClick={handleAddNew}
                            sx={{
                                transition: 'transform 0.3s ease-in-out',
                                transform: isAddIconRotated ? 'rotate(45deg)' : 'rotate(0deg)',
                                backgroundColor: isAddIconRotated ? '#f2f9ff' : 'transparent',
                                '&:hover': {
                                    backgroundColor: isAddIconRotated ? '#f2f9ff' : 'action.hover'
                                }
                            }}
                        >
                            <AddOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {/* Form Tambah Data Baru */}
                {showAddForm && (
                    <Paper sx={{ p: 2, mb: 2, bgcolor: '#f2f9ff' }} elevation={0}>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            Tambah Data Baru
                        </Typography>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                            <TextField
                                label="Lokasi"
                                size="small"
                                value={newData.lokasi}
                                onChange={(e) => handleNewDataChange('lokasi', e.target.value)}
                                required
                                fullWidth
                                error={isLokasiExists(newData.lokasi)}
                                helperText={isLokasiExists(newData.lokasi) ? "Lokasi sudah ada dalam database" : ""}
                            />
                            <FormControl fullWidth size="small">
                                <InputLabel>Ka.Div</InputLabel>
                                <Select
                                    value={newData.id_kepala_wilayah}
                                    label="Ka.Div"
                                    onChange={(e) => handleNewDataChange('id_kepala_wilayah', e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Pilih Ka.Div</em>
                                    </MenuItem>
                                    {kepalaWilayahList.map((item) => (
                                        <MenuItem key={item.id_kepala_wilayah} value={item.id_kepala_wilayah}>
                                            {item.nama_kepala_wilayah}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Wilayah Plant Care</InputLabel>
                                <Select
                                    value={newData.id_kepala_kebun_plant_care}
                                    label="Wilayah Plant Care"
                                    onChange={(e) => handleNewDataChange('id_kepala_kebun_plant_care', e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Pilih Wilayah Plant Care</em>
                                    </MenuItem>
                                    {plantCareList.map((item) => (
                                        <MenuItem key={item.id_kepala_kebun_plant_care} value={item.id_kepala_kebun_plant_care}>
                                            {item.nama_kepala_kebun_plant_care}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Wilayah Fruit Care</InputLabel>
                                <Select
                                    value={newData.id_kepala_kebun_fruit_care}
                                    label="Wilayah Fruit Care"
                                    onChange={(e) => handleNewDataChange('id_kepala_kebun_fruit_care', e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Pilih Wilayah Fruit Care</em>
                                    </MenuItem>
                                    {fruitCareList.map((item) => (
                                        <MenuItem key={item.id_kepala_kebun_fruit_care} value={item.id_kepala_kebun_fruit_care}>
                                            {item.nama_kepala_kebun_fruit_care}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button variant="outlined" onClick={handleCancelNew}>
                                Batal
                            </Button>
                            <Button variant="contained" onClick={handleSaveNew} disabled={isLokasiExists(newData.lokasi)}>
                                Simpan
                            </Button>
                        </Stack>
                    </Paper>
                )}

                {displayedDataList.length > 0 ? (
                    <>
                        {/* Gunakan CustomTableContainer di sini */}
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 500,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="data pj wilayah table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="ded-tch">Lokasi</TableCell>
                                        <TableCell className="ded-tch">Ka.Div</TableCell>
                                        <TableCell className="ded-tch">Wilayah Plant Care</TableCell>
                                        <TableCell className="ded-tch">Wilayah Fruit Care</TableCell>
                                        <TableCell className="ded-tch" style={{ width: '100px' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {displayedDataList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => (
                                            <TableRow key={row.firebaseKey}>
                                                {editingRow === row.firebaseKey ? (
                                                    <>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.lokasi}
                                                                disabled // Kunci input Lokasi saat edit
                                                                fullWidth
                                                                sx={{
                                                                    '& .MuiInputBase-input.Mui-disabled': {
                                                                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                                                                        backgroundColor: 'action.disabledBackground'
                                                                    }
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <FormControl fullWidth size="small">
                                                                <Select
                                                                    value={editFormData.id_kepala_wilayah}
                                                                    onChange={(e) => handleEditChange('id_kepala_wilayah', e.target.value)}
                                                                >
                                                                    <MenuItem value="">
                                                                        <em>Pilih Ka.Div</em>
                                                                    </MenuItem>
                                                                    {kepalaWilayahList.map((item) => (
                                                                        <MenuItem key={item.id_kepala_wilayah} value={item.id_kepala_wilayah}>
                                                                            {item.nama_kepala_wilayah}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </TableCell>
                                                        <TableCell>
                                                            <FormControl fullWidth size="small">
                                                                <Select
                                                                    value={editFormData.id_kepala_kebun_plant_care}
                                                                    onChange={(e) => handleEditChange('id_kepala_kebun_plant_care', e.target.value)}
                                                                >
                                                                    <MenuItem value="">
                                                                        <em>Pilih Wilayah Plant Care</em>
                                                                    </MenuItem>
                                                                    {plantCareList.map((item) => (
                                                                        <MenuItem key={item.id_kepala_kebun_plant_care} value={item.id_kepala_kebun_plant_care}>
                                                                            {item.nama_kepala_kebun_plant_care}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </TableCell>
                                                        <TableCell>
                                                            <FormControl fullWidth size="small">
                                                                <Select
                                                                    value={editFormData.id_kepala_kebun_fruit_care}
                                                                    onChange={(e) => handleEditChange('id_kepala_kebun_fruit_care', e.target.value)}
                                                                >
                                                                    <MenuItem value="">
                                                                        <em>Pilih Wilayah Fruit Care</em>
                                                                    </MenuItem>
                                                                    {fruitCareList.map((item) => (
                                                                        <MenuItem key={item.id_kepala_kebun_fruit_care} value={item.id_kepala_kebun_fruit_care}>
                                                                            {item.nama_kepala_kebun_fruit_care}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="success"
                                                                    onClick={() => handleSaveEdit(row.firebaseKey)}
                                                                >
                                                                    <CheckOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error"
                                                                    onClick={handleCancelEdit}
                                                                >
                                                                    <CloseOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </>
                                                ) : (
                                                    <>
                                                        <TableCell>
                                                            <Box>
                                                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                                    {formatText(row.lokasi)}
                                                                </div>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>{formatText(getNamaKepalaWilayah(row.id_kepala_wilayah))}</TableCell>
                                                        <TableCell>{formatText(getNamaPlantCare(row.id_kepala_kebun_plant_care))}</TableCell>
                                                        <TableCell>{formatText(getNamaFruitCare(row.id_kepala_kebun_fruit_care))}</TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="primary"
                                                                    onClick={() => handleEditClick(row)}
                                                                >
                                                                    <EditOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error"
                                                                    onClick={() => handleDelete(row.firebaseKey, row.lokasi)}
                                                                >
                                                                    <DeleteOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>
                        
                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>
                            
                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ 
                                    flexShrink: 0,
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        margin: 0
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery ? 'Tidak ada data yang sesuai dengan pencarian' : 'Tidak ada data. Klik icon "+" untuk menambahkan atau "Import Excel" untuk mengimpor data.'}
                        </Typography>
                    </Box>
                )}
            </Paper>
            
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default DataPJWilayah;